import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

export type DestinationSelection = {
  file_center: boolean;
  connect: boolean;
  research_assistant: boolean;
};

const UNCHECKED_DESTINATIONS: DestinationSelection = {
  file_center: false,
  connect: false,
  research_assistant: false,
};

const FIELDS: { key: keyof DestinationSelection; label: string }[] = [
  { key: 'file_center', label: 'File Center' },
  { key: 'connect', label: 'Connect' },
  { key: 'research_assistant', label: 'Research Assistant' },
];

const RESEARCH_BEFORE_CONNECT: { key: keyof DestinationSelection; label: string }[] = [
  { key: 'file_center', label: 'File Center' },
  { key: 'research_assistant', label: 'Research Assistant' },
  { key: 'connect', label: 'Connect' },
];

export function destinationsAvailable(documentType: string): boolean {
  return documentType !== 'potential_state_law';
}

export function submittedDestinations(): DestinationSelection {
  return { ...UNCHECKED_DESTINATIONS };
}

const DEFAULT_HELP =
  'File Center is applied now. Connect and Research Assistant wait until this file is chunked and embedded.';

export const CONNECT_PUBLIC_NOTICE =
  "Connect is public. If you select Connect, this file can appear on the city's public Connect tool. Anyone who can view that site may see it. Do not select Connect for files that should stay internal to your team.";

type DestinationCheckboxesProps = {
  documentType: string;
  value: DestinationSelection;
  onChange: (value: DestinationSelection) => void;
  help?: string;
  connectNotice?: string;
  connectRequiresResearch?: boolean;
  researchBeforeConnect?: boolean;
};

export default function DestinationCheckboxes({
  documentType,
  value,
  onChange,
  help = DEFAULT_HELP,
  connectNotice,
  connectRequiresResearch = false,
  researchBeforeConnect = false,
}: DestinationCheckboxesProps) {
  if (!destinationsAvailable(documentType)) return null;

  const fields = researchBeforeConnect ? RESEARCH_BEFORE_CONNECT : FIELDS;

  const change = (key: keyof DestinationSelection, checked: boolean) => {
    if (connectRequiresResearch && key === 'research_assistant' && !checked && value.connect) {
      return;
    }

    const next = { ...value, [key]: checked };
    if (connectRequiresResearch && key === 'connect' && checked) {
      next.research_assistant = true;
    }
    onChange(next);
  };

  return (
    <fieldset className="space-y-3">
      <legend className="text-sm font-medium">Destinations</legend>
      <p id="destination-help" className="text-xs text-muted-foreground">
        {help}
      </p>
      {fields.map((field) => {
        const id = `destination-${field.key}`;
        const researchLocked =
          connectRequiresResearch && field.key === 'research_assistant' && value.connect;
        const describedBy = field.key === 'connect' && connectNotice
          ? 'destination-help destination-connect-notice'
          : 'destination-help';
        return (
          <div key={field.key} className="space-y-1">
            <div className="flex items-center gap-2">
              <Checkbox
                id={id}
                checked={value[field.key]}
                disabled={researchLocked}
                aria-describedby={describedBy}
                onCheckedChange={(checked) => change(field.key, checked === true)}
              />
              <Label htmlFor={id}>{field.label}</Label>
            </div>
            {field.key === 'connect' && connectNotice && (
              <p id="destination-connect-notice" className="pl-6 text-xs text-muted-foreground">
                {connectNotice}
              </p>
            )}
          </div>
        );
      })}
    </fieldset>
  );
}
